import { cn } from '@/lib/utils';
import * as React from 'react';
import { BackHandler, Dimensions, GestureResponderEvent, LayoutRectangle, Modal, Pressable, StatusBar, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeOut,
  FadeOutUp,
} from 'react-native-reanimated';
import { computeDropdownPosition } from './dropdown-menu-position';
import { Text, TextProps } from './text';

interface DropdownMenuContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerLayout: LayoutRectangle | null;
  setTriggerLayout: (layout: LayoutRectangle) => void;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | undefined>(undefined);

function useDropdownMenuContext() {
  const context = React.useContext(DropdownMenuContext);
  if (!context) {
    throw new Error('DropdownMenu compound components must be used within a DropdownMenu');
  }
  return context;
}

interface DropdownMenuProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function DropdownMenu({ open: controlledOpen, onOpenChange, defaultOpen = false, children }: DropdownMenuProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const [triggerLayout, setTriggerLayout] = React.useState<LayoutRectangle | null>(null);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  return (
    <DropdownMenuContext.Provider value={{ open, onOpenChange: handleOpenChange, triggerLayout, setTriggerLayout }}>
      {children}
    </DropdownMenuContext.Provider>
  );
}

interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

function DropdownMenuTrigger({ children, asChild }: DropdownMenuTriggerProps) {
  const { onOpenChange, setTriggerLayout } = useDropdownMenuContext();
  const triggerRef = React.useRef<View>(null);

  const handleOpen = () => {
    if (triggerRef.current) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        setTriggerLayout({ x, y, width, height });
        onOpenChange(true);
      });
    }
  };

  if (asChild && React.isValidElement(children)) {
    return (
      <View ref={triggerRef} collapsable={false}>
        {React.cloneElement(children as React.ReactElement<{ onPress?: () => void }>, {
          onPress: handleOpen,
        })}
      </View>
    );
  }

  return (
    <Pressable ref={triggerRef} onPress={handleOpen} accessibilityRole="button">
      {children}
    </Pressable>
  );
}

interface DropdownMenuContentProps extends React.ComponentPropsWithoutRef<typeof View> {
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
}

function DropdownMenuContent({ children, className, align = 'end', side = 'bottom', ...props }: DropdownMenuContentProps) {
  const { open, onOpenChange, triggerLayout } = useDropdownMenuContext();
  // Only the `align="center"` / `side="top"` cases need a measured size; the
  // common `align="end"`/`start`, `side="bottom"` cases are anchored to screen
  // edges and never depend on this. Visibility is NOT gated on measurement — on
  // native the content view's onLayout does not reliably fire inside a Modal, so
  // gating opacity on it left the menu permanently invisible (backdrop-only).
  const [contentSize, setContentSize] = React.useState({ width: 0, height: 0 });

  React.useEffect(() => {
    if (!open) {
      setContentSize({ width: 0, height: 0 });
      return;
    }

    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      onOpenChange(false);
      return true;
    });

    return () => backHandler.remove();
  }, [open, onOpenChange]);

  if (!open || !triggerLayout) return null;

  const { width: screenWidth } = Dimensions.get('window');
  // On Android a statusBarTranslucent Modal draws from the physical screen top,
  // but measureInWindow reports the trigger below the status bar — without this
  // the menu lands ~one status-bar too high (appears above the trigger).
  // StatusBar.currentHeight is Android-only (undefined on iOS/web), so this is a
  // no-op everywhere the two origins already agree.
  const topOffset = StatusBar.currentHeight ?? 0;
  const position = computeDropdownPosition(triggerLayout, { align, side, screenWidth, contentSize, topOffset });

  return (
    <Modal
      transparent
      visible={open}
      onRequestClose={() => onOpenChange(false)}
      statusBarTranslucent
      animationType="none"
    >
      <Pressable
        className="flex-1"
        onPress={() => onOpenChange(false)}
        accessibilityLabel="Close menu"
        accessibilityRole="button"
      >
        <Animated.View
          entering={FadeIn.duration(120)}
          exiting={FadeOut.duration(80)}
          className="absolute inset-0 bg-black/20"
        />
        <Animated.View
          entering={FadeInDown.duration(130)}
          exiting={FadeOutUp.duration(90)}
          onLayout={(e) => {
            const { width: w, height: h } = e.nativeEvent.layout;
            if (w !== contentSize.width || h !== contentSize.height) {
              setContentSize({ width: w, height: h });
            }
          }}
          style={{ position: 'absolute', ...position }}
        >
          <View
            className={cn(
              'min-w-[200px] rounded-md border border-border p-1 shadow-lg bg-popover',
              className
            )}
            accessibilityRole="menu"
            {...props}
          >
            {children}
          </View>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

interface DropdownMenuItemProps extends Omit<React.ComponentPropsWithoutRef<typeof Pressable>, 'children'> {
  inset?: boolean;
  destructive?: boolean;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const DropdownMenuItem = React.forwardRef<React.ElementRef<typeof Pressable>, DropdownMenuItemProps>(
  ({ className, inset, destructive, icon, disabled, children, onPress, ...props }, ref) => {
    const { onOpenChange } = useDropdownMenuContext();

    const handlePress = (e: GestureResponderEvent) => {
      onPress?.(e);
      onOpenChange(false);
    };

    return (
      <Pressable
        ref={ref}
        disabled={disabled}
        onPress={handlePress}
        className={cn(
          'flex flex-row items-center gap-2 rounded-sm px-2 py-2 active:bg-surface',
          inset && 'pl-8',
          disabled && 'opacity-50',
          className
        )}
        accessibilityRole="menuitem"
        accessibilityState={{ disabled: disabled ?? undefined }}
        {...props}
      >
        {icon}
        <Text
          className={cn(
            'text-sm text-popover-foreground',
            destructive && 'text-destructive'
          )}
        >
          {children}
        </Text>
      </Pressable>
    );
  }
);
DropdownMenuItem.displayName = 'DropdownMenuItem';

interface DropdownMenuCheckboxItemProps extends Omit<React.ComponentPropsWithoutRef<typeof Pressable>, 'children'> {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  children: React.ReactNode;
}

const DropdownMenuCheckboxItem = React.forwardRef<React.ElementRef<typeof Pressable>, DropdownMenuCheckboxItemProps>(
  ({ className, checked = false, onCheckedChange, disabled, children, ...props }, ref) => {
    const { onOpenChange } = useDropdownMenuContext();

    const handlePress = () => {
      onCheckedChange?.(!checked);
      onOpenChange(false);
    };

    return (
      <Pressable
        ref={ref}
        disabled={disabled}
        onPress={handlePress}
        className={cn(
          'flex flex-row items-center gap-2 rounded-sm px-2 py-2 pl-8 active:bg-accent',
          disabled && 'opacity-50',
          className
        )}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: checked ?? false, disabled: disabled ?? undefined }}
        {...props}
      >
        <View className="absolute left-2 w-4 h-4 items-center justify-center">
          {checked && (
            <Text className="text-sm text-popover-foreground">✓</Text>
          )}
        </View>
        <Text className="text-sm text-popover-foreground">{children}</Text>
      </Pressable>
    );
  }
);
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

type DropdownMenuLabelProps = TextProps & {
  inset?: boolean;
};

const DropdownMenuLabel = React.forwardRef<React.ElementRef<typeof Text>, DropdownMenuLabelProps>(
  ({ className, inset, ...props }, ref) => (
    <Text
      ref={ref}
      className={cn(
        'px-2 py-1.5 text-sm font-semibold text-popover-foreground',
        inset && 'pl-8',
        className
      )}
      {...props}
    />
  )
);
DropdownMenuLabel.displayName = 'DropdownMenuLabel';

type DropdownMenuSeparatorProps = React.ComponentPropsWithoutRef<typeof View>;

const DropdownMenuSeparator = React.forwardRef<React.ElementRef<typeof View>, DropdownMenuSeparatorProps>(
  ({ className, ...props }, ref) => (
    <View
      ref={ref}
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  )
);
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

type DropdownMenuShortcutProps = TextProps;

const DropdownMenuShortcut = React.forwardRef<React.ElementRef<typeof Text>, DropdownMenuShortcutProps>(
  ({ className, ...props }, ref) => (
    <Text
      ref={ref}
      className={cn('ml-auto text-xs tracking-widest text-muted-foreground', className)}
      {...props}
    />
  )
);
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

interface DropdownMenuGroupProps {
  children: React.ReactNode;
}

function DropdownMenuGroup({ children }: DropdownMenuGroupProps) {
  return <View>{children}</View>;
}

export {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut, DropdownMenuTrigger
};
export type {
  DropdownMenuCheckboxItemProps, DropdownMenuContentProps, DropdownMenuGroupProps, DropdownMenuItemProps, DropdownMenuLabelProps, DropdownMenuProps, DropdownMenuSeparatorProps,
  DropdownMenuShortcutProps, DropdownMenuTriggerProps
};

